import { useCallback, useEffect, useRef, useState } from "react";
import Input from "@mui/joy/Input";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { SxProps } from "@mui/joy/styles/types";
import Loader from "../Loader/Loader";
import IconButton from "@mui/joy/IconButton";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import Tooltip from "@mui/joy/Tooltip";
import { Typography } from "@mui/joy";
import { ErrorOutline } from "@mui/icons-material";

interface Props {
  setValue: (input: string) => void;
  placeholder?: string;
  sx?: SxProps;
  errorSx?: SxProps;
  value: string;
  isLoading?: boolean;
  disabled?: boolean;
  showClear?: boolean;
  errorText?: string;
}

const DebounceSearchInput = ({
  value,
  setValue,
  placeholder,
  errorSx,
  sx,
  isLoading,
  disabled,
  showClear,
  errorText,
}: Props) => {
  const [input, setInput] = useState(value);
  const ref = useRef<any>(null);

  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setValueRef.current(input as string);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [input]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  useEffect(() => {
    // sync value with local
    setInput(value);
  }, [value]);

  return (
    <>
      <Input
        startDecorator={<SearchOutlinedIcon />}
        slotProps={{
          input: {
            ref,
          },
        }}
        variant="outlined"
        size="sm"
        sx={[
          {
            maxWidth: "600px",
            p: 1,
            flex: 1,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        placeholder={placeholder}
        value={input}
        onChange={handleChange}
        disabled={disabled}
        endDecorator={
          isLoading ? (
            <Loader loaderSize="sm" />
          ) : value && showClear ? (
            <Tooltip title="Clear Search">
              <IconButton
                size="sm"
                sx={{
                  mr: 1,
                  p: 0,
                  w: "min-content",
                  h: "min-content",
                  minWidth: "min-content",
                  minHeight: "min-content",
                }}
                onClick={() => {
                  setValue("");
                  ref.current.focus();
                }}
              >
                <CloseOutlined />
              </IconButton>
            </Tooltip>
          ) : (
            <></>
          )
        }
      />
      {errorText ? (
        <Typography
          startDecorator={<ErrorOutline />}
          fontSize="inherit"
          color="danger"
          sx={[
            {
              mt: 1,
              fontSize: "sm",
            },
            ...(Array.isArray(errorSx) ? errorSx : [errorSx]),
          ]}
        >
          {errorText}
        </Typography>
      ) : null}
    </>
  );
};
export default DebounceSearchInput;
